import type { ReactNode } from "react";

/**
 * Os primitivos do LEIAUTE DENSO — a prévia `docs/previas/painel-denso.html` virando produto.
 *
 * O pedido do owner (2026-09-03): *"cards bonitos, compactos, organizados, estruturados, com botões
 * para abrir os detalhes... evitar textos gigantes explicativos... menos é mais."* E a regra que
 * saiu da medição das telas: **o cartão mostra o FATO; a explicação fica atrás de um clique.**
 *
 * Três degraus para a prosa, do mais leve ao mais pesado:
 *   1. `Porque` — o "?" ao lado de um rótulo, com a frase curta no `title`;
 *   2. `Gaveta` — um `<details>` para o parágrafo inteiro;
 *   3. um botão "Detalhes" que abre a tela cheia (isso é de cada tela).
 *
 * ⚠️ NENHUMA FRASE É APAGADA ao usar isto. "Menos é mais" é sobre a primeira visão, não sobre o
 * texto existir — as frases do produto são boas, e continuam encontráveis (inclusive por teste).
 */

/** Grade fluida: os cartões ocupam o que houver e refluem sozinhos. `min(…,100%)` para não estourar a 375px. */
export function Grade(props: { readonly children: ReactNode; readonly minimo?: number; readonly className?: string }) {
  const min = props.minimo ?? 272;
  return (
    <div
      className={`grid gap-3 ${props.className ?? ""}`.trim()}
      style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, 100%), 1fr))` }}
    >
      {props.children}
    </div>
  );
}

/**
 * O "?": a explicação que deixa de ocupar a primeira visão sem deixar de existir.
 * É um `<button>` e não um `<span>` para ter foco de teclado; o texto vive no `title` e no
 * `aria-label`, então leitor de tela o lê inteiro.
 */
export function Porque(props: { readonly texto: string }) {
  return (
    <button
      type="button"
      title={props.texto}
      aria-label={props.texto}
      className="inline-grid h-[15px] w-[15px] shrink-0 cursor-help place-items-center rounded-full border border-[color:var(--borda-forte)] text-[10px] font-bold leading-none text-[color:var(--tinta-fraca)] hover:border-[color:var(--acao-texto)] hover:text-[color:var(--acao-texto)]"
    >
      ?
    </button>
  );
}

/** A gaveta: onde a prosa vai morar. Fechada por padrão; o `summary` é a única linha que se vê. */
export function Gaveta(props: { readonly titulo: string; readonly children: ReactNode; readonly aberta?: boolean }) {
  return (
    <details className="group border-t border-[color:var(--borda)] pt-2" {...(props.aberta ? { open: true } : {})}>
      <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs font-medium text-[color:var(--acao-texto)] [&::-webkit-details-marker]:hidden">
        <span aria-hidden className="inline-block text-[9px] transition-transform group-open:rotate-90">▶</span>
        {props.titulo}
      </summary>
      <div className="mt-2 max-w-[62ch] text-xs text-[color:var(--tinta-fraca)]">{props.children}</div>
    </details>
  );
}

/** Pares rótulo/valor, densos — a ficha do cartão. Não vira parágrafo. */
export function Ficha(props: { readonly itens: readonly { readonly rotulo: ReactNode; readonly valor: ReactNode }[] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
      {props.itens.map((i, k) => (
        <div key={k} className="contents">
          <dt className="text-[color:var(--tinta-fraca)]">{i.rotulo}</dt>
          <dd className="text-right tabular-nums text-[color:var(--tinta)]">{i.valor}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Uma escolha como CARTÃO selecionável — o alvo de clique inteiro, em vez de um radio de 13px com
 * um parágrafo ao lado. É um `<label>` envolvendo o `<input type="radio">` de verdade: teclado,
 * leitor de tela e `name`/`checked` continuam sendo os do navegador.
 */
export function Escolha(props: {
  readonly nome: string;
  readonly valor: string;
  readonly marcada: boolean;
  readonly aoEscolher: () => void;
  readonly titulo: string;
  readonly descricao: string;
  /**
   * A opção que existe e não serve AQUI.
   *
   * Ela aparece recusada em vez de sumir: quem a procura — porque leu, ou viu no concorrente —
   * precisa ler por que ela não serve, e opção ausente é indistinguível de opção inexistente. Quem
   * diz o motivo é quem chama.
   */
  readonly desabilitada?: boolean;
}) {
  return (
    <label
      className={[
        "flex flex-col gap-0.5 rounded-xl border px-3 py-2.5 transition",
        props.desabilitada ? "cursor-not-allowed" : "cursor-pointer",
        props.marcada
          ? "border-[color:var(--acao)] bg-[color:var(--acao-clara)] shadow-[0_0_0_1px_var(--acao)]"
          : props.desabilitada
            ? "border-[color:var(--borda)] bg-[color:var(--vidro-superficie)]"
            : "border-[color:var(--borda)] bg-[color:var(--vidro-controle)] hover:border-[color:var(--borda-forte)]",
      ].join(" ")}
    >
      {/*
        A OPACIDADE FICA NO RADIO, NUNCA NO TEXTO — quinta ocorrência da mesma aritmética neste
        repositório, e a última custou 2,31:1 medidos nesta mesma família de controle. O tom de
        "não serve" vem do tom herdado (`--tinta-fraca`), que já é medido.
      */}
      <span
        className={
          "flex items-center gap-2 text-sm font-semibold " +
          (props.desabilitada ? "text-[color:var(--tinta-fraca)]" : "text-[color:var(--tinta-forte)]")
        }
      >
        <input
          type="radio"
          name={props.nome}
          value={props.valor}
          checked={props.marcada}
          disabled={props.desabilitada}
          onChange={props.aoEscolher}
          className={"accent-[var(--acao-forte)]" + (props.desabilitada ? " opacity-60" : "")}
        />
        {props.titulo}
      </span>
      <span className="pl-5 text-xs text-[color:var(--tinta-fraca)]">{props.descricao}</span>
    </label>
  );
}
